import { Avatar, IconButton, Menu, MenuItem } from '@mui/material';
import {
  getPricingType,
  postShowTrialEndModal,
} from '@Store/feature/pricingSlice';
import React, { FC, useState } from 'react';

import { Icons } from '../../../assets/SvgIcons';
import { ComponentHelper, useEffectAsync } from '../../../helper/Infra';
import { ICTask, MergefloDealspaceUsers } from '../../../helper/Interfaces';
import { MergefloColors } from '../../../helper/MergefloColors';
import { getPlayBookDetail } from '../../../store/feature/playBookDetailSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

type UserAvatarCardProps = {
  task: ICTask | undefined;
  updateTask?: (currentTask: ICTask, username: string) => void;
  disabled?: boolean;
  buttonStyles?: any;
  avatarStyles?: any;
};

export const UserAvatarCard: FC<UserAvatarCardProps> = ({
  task,
  updateTask,
  disabled,
  buttonStyles,
  avatarStyles,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { userList: playbookUsers } = useAppSelector(getPlayBookDetail);
  const [userList, setUserList] = useState<MergefloDealspaceUsers[]>([]);
  const [currentUser, setCurrentUser] = useState<
    MergefloDealspaceUsers | undefined
  >(undefined);
  const dispatch = useAppDispatch();
  const componentHelper = new ComponentHelper();
  const pricingReducer = useAppSelector(getPricingType);
  const isTrialEnd = componentHelper.isDealspaceTrialEnd(pricingReducer);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isTrialEnd) {
      dispatch(postShowTrialEndModal(true));
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [taskUsername, setTaskUsername] = useState<string>(
    currentUser ? `${currentUser.name} ${currentUser.surname}` : ''
  );
  const mergefloColors = new MergefloColors();

  const [, setuser] = useState<MergefloDealspaceUsers | undefined>(currentUser);

  useEffectAsync(async () => {
    if (task?.playbookId) {
      if (task.type === 'dependency') {
        setUserList(
          playbookUsers.filter(
            i => i.functionalAreaId === task.dependencyTarget
          ) ?? []
        );
        setCurrentUser(
          playbookUsers.find(
            i =>
              i.username === task?.assignee &&
              i.functionalAreaId === task.dependencyTarget
          )
        );
        const currentUserFound = playbookUsers.find(
          i =>
            i.username === task?.assignee &&
            i.functionalAreaId === task.dependencyTarget
        );
        if (currentUserFound) {
          setTaskUsername(
            currentUserFound.name
              ? `${currentUserFound.name} ${currentUserFound.surname}`
              : `${currentUserFound.username}`
          );
        }
      } else {
        setUserList(
          playbookUsers.filter(i => i.playbookId === task.playbookId) ?? []
        );
        setCurrentUser(
          playbookUsers.find(
            i =>
              i.username === task?.assignee && i.playbookId === task.playbookId
          )
        );
        const currentUserFound = playbookUsers.find(
          i => i.username === task?.assignee && i.playbookId === task.playbookId
        );
        if (currentUserFound) {
          setTaskUsername(
            currentUserFound.name
              ? `${currentUserFound.name} ${currentUserFound.surname}`
              : `${currentUserFound.username}`
          );
        }
      }
    }
  }, [task]);

  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: mergefloColors.getUserColors(name),
      },
      children: name.trim().includes(' ')
        ? `${name.split(' ')[0][0].toUpperCase()}${name
            .split(' ')[1][0]
            .toUpperCase()}`
        : name.trim().length > 0
        ? `${name[0].toUpperCase()}`
        : '',
    };
  }

  return (
    <>
      {taskUsername ? (
        <IconButton
          aria-label="delete"
          onClick={handleClick}
          style={{
            padding: 0,
            width: 32,
            height: 32,
            ...(buttonStyles && { ...buttonStyles }),
          }}
          disabled={disabled}
        >
          <Avatar
            style={{
              fontSize: 14,
              width: 32,
              height: 32,
              ...(avatarStyles && { ...avatarStyles }),
            }}
            {...stringAvatar(taskUsername)}
          />
        </IconButton>
      ) : (
        <button
          className="button-round-dashed"
          style={{ ...(buttonStyles && { ...buttonStyles }) }}
          onClick={handleClick}
          disabled={disabled}
        >
          <Icons.Person />
        </button>
      )}

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        className={'customUserPopover'}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: '#091e42',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          key={'0'}
          onClick={() => {
            setTaskUsername('');
            setuser(undefined);
            if (updateTask && task) {
              updateTask(task, '');
            }
          }}
        >
          Unassigned
        </MenuItem>
        {userList && userList.length > 0 ? (
          userList.map((user, index) => {
            return (
              <MenuItem
                key={index}
                onClick={() => {
                  setTaskUsername(`${user.name} ${user.surname}`);
                  setuser(user);
                  if (updateTask && task) {
                    updateTask(task, user.username);
                  }
                }}
              >
                <Avatar
                  style={{ fontSize: 14 }}
                  {...stringAvatar(
                    user.name
                      ? `${user.name} ${user.surname}`
                      : `${user.username}`
                  )}
                />
                {user.name
                  ? `${user.name} ${user.surname}`
                  : `${user.username}`}
              </MenuItem>
            );
          })
        ) : (
          <div></div>
        )}
      </Menu>
    </>
  );
};

export default UserAvatarCard;
